<template>
  <!--
    ── Lo que cuesta el asistente ────────────────────────────────────────────
    Una fila por acción (metering.js): crear una orden, una consulta, un turno
    de chat. La cifra grande es la que tiene que bajar — lo que cuesta de media
    una orden de trabajo creada —, y al lado, cuántas se resolvieron por reglas,
    con una sola extracción o con el agente entero, que es lo que la mueve.
  -->
  <div class="costs">
    <div class="costs__head">
      <p class="costs__title">{{ t('assistant.costs.title') }}</p>
      <div class="costs__range" role="group" :aria-label="t('assistant.costs.range')">
        <button v-for="option in [7, 30, 90]" :key="option" type="button" class="chip" :class="{ 'chip--on': days === option }" @click="load(option)">
          {{ t('assistant.costs.days', { count: option }) }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="costs__body">
      <UiSkeleton v-for="n in 3" :key="n" height="56px" />
    </div>
    <p v-else-if="failed" class="costs__muted" role="alert">{{ t('assistant.costs.failed') }}</p>

    <div v-else class="costs__body">
      <div class="big">
        <div class="big__item big__item--main">
          <span class="big__value">{{ data.orders.avgCostEur === null ? '—' : euros(data.orders.avgCostEur) }}</span>
          <span class="big__label">{{ t('assistant.costs.perOrder') }}</span>
          <span class="big__sub">{{ t('assistant.costs.ordersCreated', { count: data.orders.created }) }}</span>
        </div>
        <div class="big__item">
          <span class="big__value">{{ euros(data.total.costEur) }}</span>
          <span class="big__label">{{ t('assistant.costs.total', { count: data.total.count }) }}</span>
        </div>
      </div>

      <p v-if="!data.rows.length" class="costs__muted">{{ t('assistant.costs.empty') }}</p>

      <table v-else class="table">
        <thead>
          <tr>
            <th>{{ t('assistant.costs.kind') }}</th>
            <th class="num">{{ t('assistant.costs.count') }}</th>
            <th class="num">{{ t('assistant.costs.avg') }}</th>
            <th class="num">{{ t('assistant.costs.calls') }}</th>
            <th>{{ t('assistant.costs.path') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in data.rows" :key="`${row.kind}-${row.status}`">
            <td>
              <span class="kind">{{ kindLabel(row.kind) }}</span>
              <span class="status" :class="`status--${row.status}`">{{ t(`assistant.costs.status.${row.status}`) }}</span>
            </td>
            <td class="num">{{ row.count }}</td>
            <td class="num">{{ euros(row.avgCostEur) }}</td>
            <td class="num">{{ row.avgLlmCalls }}</td>
            <td>
              <span class="paths">
                <span v-if="row.paths.rules" class="path path--rules" :title="t('assistant.costs.paths.rules')">{{ row.paths.rules }}</span>
                <span v-if="row.paths.extract" class="path path--extract" :title="t('assistant.costs.paths.extract')">{{ row.paths.extract }}</span>
                <span v-if="row.paths.agent" class="path path--agent" :title="t('assistant.costs.paths.agent')">{{ row.paths.agent }}</span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="legend">
        <span class="path path--rules">{{ t('assistant.costs.paths.rules') }}</span>
        <span class="path path--extract">{{ t('assistant.costs.paths.extract') }}</span>
        <span class="path path--agent">{{ t('assistant.costs.paths.agent') }}</span>
      </div>

      <details v-if="data.recent.length" class="recent">
        <summary>{{ t('assistant.costs.recent') }}</summary>
        <ul>
          <li v-for="row in data.recent" :key="row.id">
            <span>{{ kindLabel(row.kind) }} · {{ t(`assistant.costs.status.${row.status}`) }}</span>
            <span class="recent__figures">
              {{ euros(row.costEur) }} · {{ row.llmCalls }} {{ t('assistant.costs.callsShort') }}
              <template v-if="row.sttSeconds"> · {{ row.sttSeconds }} s</template>
              <template v-if="row.channel === 'voice'"> · 🎙</template>
            </span>
          </li>
        </ul>
      </details>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiSkeleton, api } from '@general/ui-kit'

const { t, te, locale } = useI18n()

const days = ref(30)
const data = ref(null)
const loading = ref(true)
const failed = ref(false)

// Céntimos y décimas de céntimo: una orden cuesta fracciones de euro.
const euros = (value) => new Intl.NumberFormat(locale.value, { style: 'currency', currency: 'EUR', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(Number(value) || 0)

/** 'work-orders.new_order' → «Nueva orden de trabajo»; lo desconocido, tal cual. */
function kindLabel(kind) {
  const key = `assistant.costs.kinds.${String(kind).replace(/\./g, '_')}`
  return te(key) ? t(key) : kind
}

async function load(range = days.value) {
  days.value = range
  loading.value = true
  failed.value = false
  try {
    data.value = await api.get(`/assistant/costs?days=${range}`)
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => load())
</script>

<style scoped>
.costs { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); }
.costs__head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--space-2); }
.costs__title { margin: 0; font-weight: 600; }
.costs__range { display: flex; gap: 6px; }
.costs__body { display: flex; flex-direction: column; gap: var(--space-3); }
.costs__muted { color: var(--color-text-mute); margin: 0; }

.chip {
  padding: 4px 10px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: inherit;
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
}
.chip--on { border-color: var(--color-primary); background: var(--color-primary-soft); color: var(--color-primary); }

.big { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr)); gap: var(--space-2); }
.big__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}
.big__item--main { border-color: var(--color-primary); background: var(--color-primary-soft); }
.big__value { font-size: 1.4rem; font-weight: 600; font-variant-numeric: tabular-nums; }
.big__label { font-size: var(--text-xs); color: var(--color-text-soft); }
.big__sub { font-size: var(--text-xs); color: var(--color-text-mute); }

.table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
.table th { text-align: left; font-weight: 500; color: var(--color-text-mute); font-size: var(--text-xs); padding: 4px 6px; border-bottom: 1px solid var(--color-border); }
.table td { padding: 6px; border-bottom: 1px solid var(--color-border); vertical-align: top; }
.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.kind { display: block; }
.status { font-size: var(--text-xs); color: var(--color-text-mute); }
.status--created, .status--answered, .status--done { color: var(--color-success); }
.status--failed { color: var(--color-danger); }

.paths { display: inline-flex; gap: 3px; }
.path { display: inline-block; padding: 1px 6px; border-radius: var(--radius-full); font-size: var(--text-xs); }
.path--rules { background: var(--color-success-soft); color: var(--color-success); }
.path--extract { background: var(--color-primary-soft); color: var(--color-primary); }
.path--agent { background: var(--color-warning-soft, var(--color-gray-100)); color: var(--color-warning, var(--color-text)); }
.legend { display: flex; flex-wrap: wrap; gap: 6px; }

.recent summary { cursor: pointer; font-size: var(--text-sm); color: var(--color-text-soft); }
.recent ul { list-style: none; margin: 6px 0 0; padding: 0; display: flex; flex-direction: column; gap: 4px; font-size: var(--text-xs); }
.recent li { display: flex; justify-content: space-between; gap: var(--space-2); border-bottom: 1px solid var(--color-border); padding: 4px 0; }
.recent__figures { color: var(--color-text-mute); white-space: nowrap; font-variant-numeric: tabular-nums; }
</style>
